import { useQuery } from '@tanstack/react-query';
import { kmsService } from '@aaas/shared';

interface UseSpaceListQueryOptions {
  idToken: string | undefined;
  skip?: number;
  limit?: number;
  enabled?: boolean;
  refetchInterval?: number | false;
}

/**
 * React Query hook for fetching KMS space list
 *
 * Features:
 * - Smart caching with stale-while-revalidate
 * - Auto refetch on window focus
 * - Optional polling (disabled by default)
 * - Request deduplication
 * - Background refetch control
 */
export const useSpaceListQuery = ({
  idToken,
  skip = 0,
  limit = 50,
  enabled = true,
  refetchInterval = false, // Default: no polling
}: UseSpaceListQueryOptions) => {
  return useQuery({
    queryKey: ['kms-spaces', skip, limit],
    queryFn: async () => {
      if (!idToken) {
        throw new Error('Authentication token required');
      }

      return kmsService.listSpaces(idToken, {
        skip,
        limit,
      });
    },
    enabled: enabled && !!idToken,
    // Polling config
    refetchInterval,
    refetchIntervalInBackground: false, // Don't poll when tab hidden
    // Caching config
    staleTime: 10000, // Consider data fresh for 10 seconds
    // Auto refetch on window focus
    refetchOnWindowFocus: true,
  });
};

interface UseSpaceDetailQueryOptions {
  spaceId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
}

/**
 * React Query hook for fetching single KMS space details
 */
export const useSpaceDetailQuery = ({
  spaceId,
  idToken,
  enabled = true,
}: UseSpaceDetailQueryOptions) => {
  return useQuery({
    queryKey: ['kms-space', spaceId],
    queryFn: async () => {
      if (!idToken || !spaceId) {
        throw new Error('Space ID and authentication token required');
      }

      const response = await kmsService.getSpace(idToken, spaceId);
      return response.data;
    },
    enabled: enabled && !!idToken && !!spaceId,
    staleTime: 30000, // Fresh for 30 seconds
    refetchOnWindowFocus: true,
  });
};

interface UseDocumentListQueryOptions {
  spaceId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
  refetchInterval?: number | false;
  sourceFilter?: string;
}

/**
 * React Query hook for fetching KMS documents list
 */
export const useDocumentListQuery = ({
  spaceId,
  idToken,
  enabled = true,
  refetchInterval = 5000, // Auto-refresh every 5 seconds by default
  sourceFilter,
}: UseDocumentListQueryOptions) => {
  return useQuery({
    queryKey: ['kms-documents', spaceId, sourceFilter],
    queryFn: async () => {
      if (!idToken || !spaceId) {
        throw new Error('Space ID and authentication token required');
      }

      return kmsService.listDocuments(idToken, spaceId, { sourceFilter });
    },
    enabled: enabled && !!idToken && !!spaceId,
    refetchInterval, // Auto-refresh to track upload status changes
    refetchIntervalInBackground: false, // Don't poll when tab hidden
    staleTime: 3000, // Consider data fresh for 3 seconds
    refetchOnWindowFocus: true,
  });
};
